import { Link } from 'react-router-dom';
import { useState } from 'react';
import { api } from '../../api.js';
import ConfirmModal from '../ui/ConfirmModal.jsx';
import { EmptyState, Pill, Section } from '../ui/index.jsx';
import IdentifierModal from './IdentifierModal.jsx';
import VerifiedBadge from '../ui/VerifiedSeal.jsx';
import { SOURCE_LABEL, TYPE_LABELS, VERIFICATION, VISIBILITY_SHORT } from './identityMeta.js';

function IdentifierRows({ rows, editable, onEdit, onRemove, showSource, extra, hideVisibility, canEditRow }) {
  // Inherited rows: only an Edit (visibility and label) on the ones this person may change;
  // removing one is done where it belongs (company, brand, supplier)
  const actions = editable || (canEditRow && rows.some(canEditRow));
  return (
    <div className="table-scroll">
      <table className="table table-stack">
        <thead>
          <tr>
            <th>Type</th><th>Value</th><th>Used for</th>{!hideVisibility && <th>Visibility</th>}<th>{extra ? extra.header : 'Ownership'}</th>
            {showSource && <th>From</th>}
            {actions && <th><span className="sr-only">Actions</span></th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((i) => {
            const v = VERIFICATION[i.verification] || VERIFICATION.unverified;
            return (
              <tr key={i.id}>
                <td data-label="Type">{TYPE_LABELS[i.type]}</td>
                <td data-label="Value" className="mono">{i.display_value}</td>
                <td data-label="Used for" className="muted">{i.label || '—'}</td>
                {!hideVisibility && <td data-label="Visibility">{VISIBILITY_SHORT[i.visibility]}</td>}
                <td data-label={extra ? extra.header : 'Ownership'}>
                  {extra ? extra.render(i) : i.verification === 'verified' ? <VerifiedBadge /> : (
                    <span className="fact-confirm">
                      <Pill tone={v.tone}>{v.label}</Pill>
                      {/* A domain-based channel becomes verified when its domain is proven */}
                      {['email_domain', 'web_domain', 'email_address'].includes(i.type) && i.verification !== 'verified' && (
                        <Link className="small" to="/company">Prove it’s yours →</Link>
                      )}
                    </span>
                  )}
                </td>
                {showSource && <td data-label="From" className="small">{SOURCE_LABEL[i.source?.kind]}: {i.source?.name}</td>}
                {editable && (
                  <td className="right nowrap row-actions">
                    <button type="button" className="btn btn-small btn-secondary" onClick={() => onEdit(i)}>Edit</button>
                    <button type="button" className="btn btn-small btn-danger-ghost" onClick={() => onRemove(i)}>Remove</button>
                  </td>
                )}
                {!editable && actions && (
                  <td className="right nowrap row-actions">
                    {canEditRow(i)
                      ? <button type="button" className="btn btn-small btn-secondary" onClick={() => onEdit(i)} title={`Change it for ${i.source?.name || 'its owner'}`}>Edit</button>
                      : i.source?.kind === 'person' ? <Link className="small" to="/company/people">On People</Link> : null}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The identifiers one company / brand / event owns: table + Add (modal) + Edit (modal)
 * + Remove (confirm). Optional `inherited` rows are shown read-only underneath.
 */
export default function IdentifiersSection({
  title = 'Official channels', description, basePath, ownerName, initial, inherited, canEdit = true, onChange,
  inheritedTitle = 'Inherited', inheritedDescription,
  // Inherited rows can be edited (visibility, label) where they belong: pass the company id
  // and whether this person may (owners and admins)
  organisationId = null, canEditInherited = false,
  // People: only some types, a fixed visibility note instead of the choice, and an
  // extra column (e.g. confirmation status) in place of "Ownership"
  types, fixedVisibility, modalDescription, extra, hideVisibility = false, addLabel = 'Add channel',
}) {
  const [rows, setRows] = useState(initial || []);
  const [inheritedRows, setInheritedRows] = useState(inherited || []);
  const [modal, setModal] = useState(null);   // { kind: 'add' } | { kind: 'edit', identifier } | { kind: 'remove', identifier } | { kind: 'edit_inherited', identifier }
  // A person's details keep their fixed visibility (set on the People page)
  const canEditRow = (i) => canEditInherited && !!organisationId && !!i.entity_id && i.source?.kind !== 'person';

  const update = (next) => { setRows(next); onChange?.(next); };
  const saved = (identifier, editing) => {
    update(editing ? rows.map((r) => (r.id === identifier.id ? identifier : r)) : [...rows, identifier]);
    setModal(null);
  };
  const remove = async () => {
    const id = modal.identifier.id;
    await api(`${basePath}/${id}`, { method: 'DELETE' });
    update(rows.filter((r) => r.id !== id));
    setModal(null);
  };

  return (
    <>
      <Section
        title={title}
        description={description}
        actions={canEdit && <button type="button" className="btn btn-primary btn-small" onClick={() => setModal({ kind: 'add' })}>{addLabel}</button>}
        flush={rows.length > 0}
      >
        {rows.length === 0 ? (
          <EmptyState
            title="Nothing listed yet"
            action={canEdit && <button type="button" className="btn btn-secondary" onClick={() => setModal({ kind: 'add' })}>{addLabel}</button>}
          >
            {types?.length && !types.includes('web_domain')
              ? 'Add the addresses and numbers they contact people from.'
              : 'Add the email domain, website, phone numbers and bank details people should trust. Anything not listed is answered “cannot be verified”.'}
          </EmptyState>
        ) : (
          <IdentifierRows rows={rows} editable={canEdit} extra={extra} hideVisibility={hideVisibility}
            onEdit={(identifier) => setModal({ kind: 'edit', identifier })}
            onRemove={(identifier) => setModal({ kind: 'remove', identifier })} />
        )}
      </Section>

      {inheritedRows.length > 0 && (
        <Section title={inheritedTitle} description={inheritedDescription} flush>
          <IdentifierRows rows={inheritedRows} showSource canEditRow={canEditRow} onEdit={(identifier) => setModal({ kind: 'edit_inherited', identifier })} />
        </Section>
      )}
      {modal?.kind === 'edit_inherited' && (
        <IdentifierModal basePath={`/portal/organisations/${organisationId}/entities/${modal.identifier.entity_id}/identifiers`}
          ownerName={modal.identifier.source?.name || ownerName} identifier={modal.identifier}
          description={`This belongs to ${modal.identifier.source?.name || 'your company'}: the change applies everywhere it counts, not only for ${ownerName}.`}
          onClose={() => setModal(null)}
          onSaved={(saved) => {
            // Keep where it comes from (the API returns the identifier alone)
            setInheritedRows(inheritedRows.map((r) => (r.id === saved.id ? { ...r, ...saved, source: r.source, label: saved.label ?? r.label } : r)));
            setModal(null);
          }} />
      )}

      {(modal?.kind === 'add' || modal?.kind === 'edit') && (
        <IdentifierModal basePath={basePath} ownerName={ownerName} identifier={modal.identifier}
          types={types} fixedVisibility={fixedVisibility} description={modalDescription}
          onClose={() => setModal(null)} onSaved={saved} />
      )}
      {modal?.kind === 'remove' && (
        <ConfirmModal title="Remove this channel?" confirmLabel="Remove" danger onClose={() => setModal(null)} onConfirm={remove}>
          <p><strong className="mono">{modal.identifier.display_value}</strong> will no longer count as {ownerName}&rsquo;s. People who check it will be told it cannot be verified.</p>
        </ConfirmModal>
      )}
    </>
  );
}
